import { ActivityLogInfo, PaymentProps, PaymentSuccess } from '@app/types';
import React from 'react';
import {
  AddCostItem,
  Container,
  Cost,
  CostInput,
  CostInputContainer,
  CostItemContainer,
  CostItemInput,
  CostValueContainer,
  PaymentFooter,
  Title,
  TitleContainer,
  TitleValueContainer,
  TrashIcon,
  Value,
  Amount,
  TotalDue,
  PaymentButton,
  MessageContainer,
  Message,
  CheckMarkIcon,
  FailMessage,
  FailureMessage,
  TryAgain,
  PaymentInfo,
  CardNumber,
  PaidBy,
  Completed,
  PayeeInfoContainer,
  CardType,
  CardBrandIcon,
  CardData,
  CardInfo,
  InfoHead,
  PaymentError,
} from '@app/modules/payment-due/payment-due-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  faCcAmex,
  faCcDinersClub,
  faCcDiscover,
  faCcJcb,
  faCcMastercard,
  faCcVisa,
} from '@fortawesome/free-brands-svg-icons';
import { Button, CurrencyInput, Dialog, Icon, Input } from '@app/components';
import moment from 'moment';
import {
  ErrorMessage,
  Seperator,
} from '../transaction-form/transactions-form-components';
import Payment from './payment';
import { useTransactionPayment } from '@app/graphql';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { currencyFormat } from '@app/utils';
import { checkCostItemsPermission } from '@app/utils/permissions';

type Props = {
  payments: PaymentProps[];
  showAdd?: boolean;
  setCostItems?: any;
  userInfo?: any;
  transactionId?: string;
  allowEdit?: boolean;
  activityLog?: ActivityLogInfo[];
  fromDetailsTab?: boolean;
  fromCreateTab?: boolean;
  setActivityLog?: any;
  register?: any;
  isSubmitted?: boolean;
  errors?: any;
  addTaxCostItem?: { add: boolean; amount: string };
  transferTaxAmount?: string;
};

export default function PaymentDue({
  payments,
  showAdd,
  setCostItems,
  transactionId,
  userInfo,
  allowEdit,
  activityLog,
  fromDetailsTab,
  setActivityLog,
  register,
  isSubmitted,
  addTaxCostItem,
  transferTaxAmount,
}: Props) {
  const cardTypes: any = {
    mastercard: faCcMastercard,
    amex: faCcAmex,
    visa: faCcVisa,
    discover: faCcDiscover,
    diners: faCcDinersClub,
    jcb: faCcJcb,
    other: faCreditCard,
  };
  const stripeKey: string =
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    'pk_test_51Jx3ehIPiUyqKaEkBYXStG4adhoEtaGj4lwfwCw92zh0tadnpIo54NBTc1Xc5ADjYeuvEoDKs4L4Z4Qp1QiRPHXY00fI4i1mtv';

  const stripePromise = loadStripe(stripeKey);
  const [showDue, setShowDue] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [showPaymentInfo, setShowPaymentInfo] = React.useState(false);
  const [showPayment, setShowPayment] = React.useState(false);
  const { firebaseId = '', email = '' } = userInfo;
  const [makeTransactionPayment] = useTransactionPayment();
  const [payeeInfo, setPayeeInfo] = React.useState<any>([]);
  const [paymentStatus, setPaymentStatus] = React.useState<PaymentSuccess>({
    show: false,
    status: '',
  });
  const [
    transactionPaymentsInfoIds,
    setTransactionPaymentsInfoIds,
  ] = React.useState<any>([]);
  const [
    newTransactionPaymentsInfo,
    setNewTransactionPaymentsInfo,
  ] = React.useState<PaymentProps[]>([]);
  const [transactionPayments, setTransactionPayments] = React.useState<
    PaymentProps[]
  >([]);

  const taxOption = {
    costItem: 'Transfer Tax Amount',
    paidAmount: 0,
    firebaseId: firebaseId || '',
    userEmail: email || '',
    cardEnding: 0,
    cardHolderName: '',
    paymentId: '',
    paymentStatus: false,
    transactionId: transactionId || '',
    dateofPayment: moment().format('YYYY-MM-DD hh:mm A'),
    cardBrand: '',
    cardType: '',
  };

  React.useEffect(() => {
    if (transferTaxAmount) {
      let vtaxItem: PaymentProps[] = payments.filter(
        (transactionPayment: PaymentProps) => {
          return (
            transactionPayment.costItem === 'Transfer Tax Amount' ||
            transactionPayment.costItem === 'Exemption Cost'
          );
        },
      );
      if (vtaxItem && vtaxItem.length) {
        let payments: PaymentProps[] = [...transactionPayments];
        //eslint-disable-next-line
        payments.map((payment: PaymentProps) => {
          if (
            payment.costItem === 'Transfer Tax Amount' ||
            payment.costItem === 'Exemption Cost'
          ) {
            payment.costItem = 'Transfer Tax Amount';
            payment.cost = transferTaxAmount.replace(/,/g, '');
          }
        });
        setTransactionPayments(payments);
      }
    }
    //eslint-disable-next-line
  }, [transferTaxAmount, payments]);

  React.useEffect(() => {
    if (addTaxCostItem?.add) {
      let vtaxItem: PaymentProps[] = payments.filter(
        (transactionPayment: PaymentProps) => {
          return transactionPayment.costItem === 'Transfer Tax Amount';
        },
      );
      if (vtaxItem && !vtaxItem.length) {
        setCostItems(
          transactionPayments.concat([
            {
              ...taxOption,
              cost: currencyFormat(addTaxCostItem.amount.split('$')[0]),
            },
          ]),
        );
        setTransactionPayments(
          transactionPayments.concat([
            {
              ...taxOption,
              cost: currencyFormat(addTaxCostItem.amount.split('$')[0]),
            },
          ]),
        );
      }
      if (vtaxItem && vtaxItem.length) {
        let payments: PaymentProps[] = [...transactionPayments];
        //eslint-disable-next-line
        payments.map((payment: PaymentProps) => {
          if (payment.costItem === 'Transfer Tax Amount') {
            payment.cost = addTaxCostItem.amount.replace(/,/g, '');
          }
        });
        setTransactionPayments(payments);
      }
    }
    //eslint-disable-next-line
  }, [addTaxCostItem, payments]);
  React.useEffect(() => {
    if (payments && payments.length) {
      setTransactionPayments(payments);
      const unPaidPayments: PaymentProps[] = payments.filter(
        (payment: PaymentProps) => {
          return payment.paymentStatus === false;
        },
      );
      if (unPaidPayments && unPaidPayments.length && transactionId) {
        setShowDue(true);
      } else {
        if (transactionId) {
          setPayeeInfo(payments && payments.length ? payments[0] : []);
          setShowPaymentInfo(true);
        }
      }
    }
  }, [payments, transactionId]);

  React.useEffect(() => {
    if (paymentStatus.status === 'success') {
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    }
  }, [paymentStatus]);

  /**
   * to add a new cost item
   */
  function addCostItem() {
    var costItems: any = transactionPayments.concat({
      costItem: '',
      cost: '',
      paidAmount: 0,
      firebaseId: firebaseId || '',
      userEmail: email || '',
      cardEnding: 0,
      cardHolderName: '',
      paymentId: '',
      paymentStatus: false,
      transactionId: transactionId || '',
      dateofPayment: moment().format('YYYY-MM-DD hh:mm A'),
      cardBrand: '',
      cardType: '',
    });
    setTransactionPayments(costItems);
  }
  /**
   *
   * @param type key of changed element
   * @param e event
   * @param key index of the changed element
   */
  function onChange(type: string, e: any, index: any) {
    let rows: any = [...transactionPayments];
    if (!/^\s+$/.test(type === 'costItem' ? e.target.value : e)) {
      if (type === 'costItem') {
        rows[index].costItem = e.target.value;
        setTransactionPayments([...rows]);
      } else if (type === 'cost') {
        rows[index].cost = e;
        setTransactionPayments([...rows]);
      }
    }
    setTimeout(() => {
      setCostItems(rows);
    }, 100);
  }

  /**
   *
   * @param index index of the item
   */
  function removeCostItem(index: any) {
    let items: PaymentProps[] = [];
    if (index !== -1) {
      transactionPayments.splice(index, 1);
      items = items.concat(transactionPayments);
      setTransactionPayments(items);
    }
  }

  /**
   *
   * @returns to get total amount of cost items
   */
  function getTotalAmount() {
    let totalAmount: number = 0;
    if (transactionPayments && transactionPayments.length) {
      totalAmount = transactionPayments
        .map(payment =>
          payment.cost && !payment.paymentStatus ? Number(payment.cost) : 0,
        )
        .reduce((prev: number, next: number) => prev + next);
    }
    return totalAmount ? Number(totalAmount.toFixed(2)) : 0;
  }

  /**
   * to enable payment popup
   */
  function payment() {
    const newPayments: PaymentProps[] =
      transactionPayments && transactionPayments.length
        ? transactionPayments.filter((payment: any) => {
            return !payment._id;
          })
        : [];
    setNewTransactionPaymentsInfo(newPayments);
    const unpaidItems: PaymentProps[] =
      transactionPayments && transactionPayments.length
        ? transactionPayments.filter((p: PaymentProps) => {
            return !p.paymentStatus && p._id;
          })
        : [];
    setTransactionPaymentsInfoIds(
      unpaidItems.map((uP: PaymentProps, key: number) =>
        uP._id ? uP._id : '',
      ),
    );
    const Amount: number = getTotalAmount();
    if (Amount && Amount < 0.5) {
      setShowError(true);
    } else {
      setShowPayment(true);
    }
  }

  /**
   * to make payment  based on transaction id
   */
  async function makePayment(payload: any, cardHolderName: string) {
    const { paymentMethod } = payload;
    const { card } = paymentMethod;
    const log: any = {
      transactionId: transactionId,
      firebaseId: userInfo.firebaseId,
      item: 'Payment Details',
      description: `has been updated by`,
    };
    const newTrans: PaymentProps[] =
      newTransactionPaymentsInfo && newTransactionPaymentsInfo.length
        ? newTransactionPaymentsInfo.map(
            (payment: PaymentProps, key: number) => {
              return {
                ...payment,
                cardEnding: card ? parseInt(card.last4) : 0,
                cardType: card ? `${card.funding} card` : '',
                cardBrand: card ? card.brand : '',
                paidAmount: getTotalAmount(),
                cardHolderName: cardHolderName || '',
              };
            },
          )
        : [];
    const params: any = {
      firebaseId: userInfo && userInfo.firebaseId ? userInfo.firebaseId : '',
      paidAmount: getTotalAmount(),
      userEmail: userInfo && userInfo.email ? userInfo.email : '',
      transactionId: transactionId || '',
      cardHolderName: cardHolderName || '',
      paymentId: paymentMethod ? paymentMethod.id : '',
      cardEnding: card ? parseInt(card.last4) : 0,
      cardType: card ? `${card.funding} card` : '',
      cardBrand: card ? card.brand : '',
      dateofPayment: moment().format('YYYY-MM-DD hh:mm A'),
      activityLog: [log],
      newTransactionPaymentsInfo: newTrans,
      transactionPaymentsInfoIds: transactionPaymentsInfoIds,
    };
    await makeTransactionPayment({
      variables: params,
    })
      .then((response: any) => {
        if (
          response &&
          response.data &&
          response.data.transactionPayment &&
          response.data.transactionPayment.success
        ) {
          const logs: ActivityLogInfo[] = activityLog || [];
          const { transactionPaymentsInfo } = response.data.transactionPayment;
          if (transactionPaymentsInfo && transactionPaymentsInfo.length) {
            setTransactionPayments(transactionPaymentsInfo);
            setCostItems(transactionPaymentsInfo);
            setShowPaymentInfo(true);
            setShowDue(false);
            setPayeeInfo(transactionPaymentsInfo[0]);
          }
          if (fromDetailsTab && activityLog) {
            setActivityLog([{ ...log, user: userInfo }].concat(logs));
          }
          setPaymentStatus({ show: true, status: 'success' });
          setShowPayment(false);
          return true;
        } else {
          setPaymentStatus({ show: true, status: 'error' });
          setShowPayment(false);
          return false;
        }
      })
      .catch((error: any) => {
        setShowPayment(false);
        setPaymentStatus({ show: true, status: 'error' });
        return false;
      });
  }

  function onClosingModal() {
    setPaymentStatus({ show: false, status: '' });
    if (paymentStatus.status === 'success') {
      window.location.reload();
    }
  }

  const ErrorContent = () => {
    return (
      <>
        <FailMessage>Transaction failed</FailMessage>
        <FailureMessage>Please try a different payment method</FailureMessage>
        <TryAgain>
          <Button
            variant="warning"
            onClick={() => {
              setShowPayment(true);
              setPaymentStatus({ status: '', show: false });
            }}>
            Try again
          </Button>
        </TryAgain>
      </>
    );
  };
  return (
    <>
      {checkCostItemsPermission(userInfo) ? (
        <>
          <Container>
            {transactionPayments && transactionPayments.length
              ? transactionPayments.map(
                  (payment: PaymentProps, key: number) => {
                    return (
                      <>
                        {payment.paymentStatus || !allowEdit ? (
                          <CostItemContainer>
                            <TitleContainer>
                              <TitleValueContainer>
                                <Title>Title</Title>
                                <Value>{payment.costItem}</Value>
                              </TitleValueContainer>
                              <CostValueContainer>
                                <Cost>Cost</Cost>
                                <Value>
                                  $
                                  {payment.cost
                                    ? currencyFormat(payment.cost)
                                    : 0}
                                </Value>
                              </CostValueContainer>
                            </TitleContainer>
                          </CostItemContainer>
                        ) : (
                          <CostItemContainer>
                            <TitleValueContainer>
                              <CostItemInput>
                                <Input
                                  // showDefaultValue={true}
                                  label="Title"
                                  readOnly={
                                    payment.costItem ===
                                      'Transfer Tax Amount' ||
                                    payment.costItem === 'Exemption Cost'
                                      ? true
                                      : false
                                  }
                                  star="*"
                                  backgroundColor={'#fff'}
                                  placeholder="Enter Title"
                                  name="costItem"
                                  register={register({
                                    required: true,
                                    validate: (value: any) =>
                                      !/^\s+$/.test(value) ||
                                      'Please Enter Title',
                                  })}
                                  value={payment.costItem}
                                  onChange={(e: any) =>
                                    onChange('costItem', e, key)
                                  }
                                />
                                {isSubmitted && !payment.costItem ? (
                                  <ErrorMessage>
                                    Please Enter Title
                                  </ErrorMessage>
                                ) : null}
                              </CostItemInput>
                            </TitleValueContainer>
                            <CostValueContainer>
                              <CostInputContainer>
                                <CostInput>
                                  <CurrencyInput
                                    disabled={
                                      payment.costItem ===
                                        'Transfer Tax Amount' ||
                                      payment.costItem === 'Exemption Cost'
                                    }
                                    label="Cost"
                                    star="*"
                                    placeholder="Enter Cost"
                                    name="cost"
                                    precision={0}
                                    value={
                                      payment.cost
                                        ? payment.cost.toString()
                                        : ''
                                    }
                                    register={register({
                                      required: true,
                                    })}
                                    backgroundColor={'#fff'}
                                    onChange={(e: any) =>
                                      onChange('cost', e, key)
                                    }
                                  />
                                  {isSubmitted && !payment.cost ? (
                                    <ErrorMessage>
                                      Please enter cost
                                    </ErrorMessage>
                                  ) : null}
                                </CostInput>
                                {payment.costItem !== 'Transfer Tax Amount' &&
                                payment.costItem !== 'Exemption Cost' ? (
                                  <TrashIcon
                                    onClick={() => {
                                      removeCostItem(key);
                                    }}>
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      size={'2x'}
                                    />
                                  </TrashIcon>
                                ) : null}
                              </CostInputContainer>
                            </CostValueContainer>
                          </CostItemContainer>
                        )}
                      </>
                    );
                  },
                )
              : null}
          </Container>
          {showAdd && !showPaymentInfo && (
            <AddCostItem onClick={() => addCostItem()}>
              + Add Cost Item
            </AddCostItem>
          )}
        </>
      ) : null}
      {showDue && getTotalAmount() > 0 ? (
        <>
          <Seperator />
          <PaymentFooter>
            <TotalDue>Total Due</TotalDue>
            <Amount>${currencyFormat(getTotalAmount())}</Amount>
          </PaymentFooter>
          <PaymentButton>
            <Button onClick={() => payment()} variant="warning">
              Pay Now
            </Button>
          </PaymentButton>
          {showError ? (
            <PaymentError>
              Amount must be greater than or equal to $0.5
            </PaymentError>
          ) : null}
        </>
      ) : null}
      {showPaymentInfo && (
        <PayeeInfoContainer>
          <PaymentInfo>
            <InfoHead>
              <TotalDue>Total</TotalDue>
              <div>
                <Amount>
                  $
                  {payeeInfo && payeeInfo.paidAmount
                    ? currencyFormat(payeeInfo.paidAmount)
                    : ''}
                </Amount>
                <PaidBy>
                  Paid by{' '}
                  {payeeInfo && payeeInfo.cardHolderName
                    ? payeeInfo.cardHolderName
                    : ''}
                  ,<br></br>on{' '}
                  {moment(
                    payeeInfo && payeeInfo.dateofPayment
                      ? payeeInfo.dateofPayment
                      : '',
                  ).format('Do MMMM YYYY')}
                </PaidBy>
              </div>
            </InfoHead>
            <CardInfo>
              <CardBrandIcon>
                <FontAwesomeIcon
                  icon={
                    cardTypes[
                      (payeeInfo && payeeInfo.cardBrand
                        ? payeeInfo.cardBrand
                        : 'other'
                      ).toString()
                    ]
                  }
                  size="3x"
                  color="#1A1F71"
                />
              </CardBrandIcon>
              <CardData>
                <CardNumber>
                  **** **** ****{' '}
                  {payeeInfo && payeeInfo.cardEnding
                    ? payeeInfo.cardEnding
                    : ''}
                </CardNumber>
                <CardType>
                  {payeeInfo && payeeInfo.cardBrand
                    ? payeeInfo.cardBrand
                    : '' || '--'}
                </CardType>
                <Completed>Completed</Completed>
              </CardData>
            </CardInfo>
          </PaymentInfo>
        </PayeeInfoContainer>
      )}
      {showPayment ? (
        <Dialog title="Payment" onClose={() => setShowPayment(false)}>
          <Elements stripe={stripePromise}>
            <Payment
              amount={getTotalAmount()}
              transactionPayment={makePayment}
              onCancel={() => setShowPayment(false)}
              paymentStatus={paymentStatus}
            />
          </Elements>
        </Dialog>
      ) : null}
      {paymentStatus.show ? (
        <Dialog size="medium" onClose={() => onClosingModal()}>
          <MessageContainer>
            <CheckMarkIcon>
              <Icon
                name={
                  paymentStatus.status === 'success' ? 'checkmark' : 'alertIcon'
                }
              />
            </CheckMarkIcon>
            {paymentStatus.status === 'success' ? (
              <Message>
                Your payment was <br></br>successful!
              </Message>
            ) : paymentStatus.status === 'error' ? (
              ErrorContent()
            ) : null}
          </MessageContainer>
        </Dialog>
      ) : null}
    </>
  );
}
